import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { wishlistApi, type WishlistItem } from '@/api/wishlist.api';
import { useAuthStore, selectIsAuthenticated } from '@/store/authStore';
import { toast } from '@/store/uiStore';
import { getApiErrorMessage } from '@/api/client';
import { queryKeys } from './queryKeys';

/**
 * Small, reusable, optimistic wishlist hooks shared by every ProductCard.
 *
 * The `/wishlist` API is `requireAuth` on the backend, so the list query is
 * gated behind `isAuthenticated` — anonymous visitors render cards without
 * firing (and 401-ing) a request per card.
 */

// Stable empty set so unauthenticated / loading renders don't churn identity
// and re-trigger consumers on every render.
const EMPTY_IDS: ReadonlySet<number> = new Set<number>();

/** Set of productIds the current user has wishlisted (empty when logged out). */
export function useWishlistIds(): ReadonlySet<number> {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const { data } = useQuery({
    queryKey: queryKeys.wishlist,
    queryFn: wishlistApi.list,
    enabled: isAuthenticated,
    staleTime: 60_000,
    select: (items) => new Set(items.map((i) => i.productId)),
  });
  return data ?? EMPTY_IDS;
}

/**
 * Toggle a product in the wishlist with optimistic add/remove on the
 * `['wishlist']` cache, rollback on error, and a re-sync on settle. The same
 * cache backs `useWishlistIds`, so the heart fills/empties instantly.
 */
export function useToggleWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: number) => wishlistApi.toggle(productId),
    onMutate: async (productId: number) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.wishlist });
      const previous = queryClient.getQueryData<WishlistItem[]>(queryKeys.wishlist);

      queryClient.setQueryData<WishlistItem[]>(queryKeys.wishlist, (old) => {
        const list = old ?? [];
        const exists = list.some((i) => i.productId === productId);
        if (exists) return list.filter((i) => i.productId !== productId);
        // Optimistic stub: only `productId` matters for the id-set selector and
        // the heart fill state. The real, fully-populated item replaces this on
        // settle (invalidate → refetch).
        const stub = {
          id: -productId,
          userId: -1,
          productId,
          createdAt: new Date().toISOString(),
        } as WishlistItem;
        return [...list, stub];
      });

      return { previous };
    },
    onError: (err, _productId, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKeys.wishlist, ctx.previous);
      }
      toast.error('Could not update wishlist', getApiErrorMessage(err));
    },
    onSuccess: (res) => {
      toast.success(res.added ? 'Saved to wishlist' : 'Removed from wishlist');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wishlist });
    },
  });
}
